// ============================================================================
// ArchivioDocumenti.jsx v2 - con UPLOAD per Impresa e Attrezzature
//
// Fase 2C: aggiunge bottone "+ Carica" per ogni categoria.
// - Categorie Impresa: predefinite (durc, visura_camerale, inail, inps, cassa_edile)
// - Categorie Attrezzature: si possono creare al volo (ponteggio, libretti, ecc.)
// - Attestati e Sanita': UI in arrivo (per ora informativo)
//
// Endpoint:
//   GET  /api/documenti/lista
//   POST /api/documenti/upload (multipart: file, tipoDoc, categoria, filename)
//   GET  /api/documenti/file?path=...
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';

const NAS_URL = "";

const theme = {
  bg: "#0f1117",
  surface: "#1a1d27",
  surfaceHover: "#22263a",
  border: "#2a2e45",
  accent: "#f5a623",
  text: "#e8eaf2",
  textMuted: "#7b82a0",
  textDim: "#4a5068",
  success: "#3ecf8e",
  info: "#378ADD",
  warning: "#f5a623",
  error: "#f06060",
};

const cardSt = {
  background: theme.surface,
  border: `1px solid ${theme.border}`,
  borderRadius: 12,
  padding: 18,
  marginBottom: 12,
};

const btnPrimarioSt = {
  background: theme.accent,
  color: "#1a1d27",
  fontWeight: 700,
  fontSize: 13,
  border: "none",
  borderRadius: 8,
  padding: "8px 14px",
  cursor: "pointer",
  fontFamily: "'DM Sans',sans-serif",
};

const btnSecondarioSt = {
  background: "transparent",
  color: theme.text,
  fontWeight: 500,
  fontSize: 13,
  border: `1px solid ${theme.border}`,
  borderRadius: 8,
  padding: "8px 14px",
  cursor: "pointer",
  fontFamily: "'DM Sans',sans-serif",
};

const TABS = [
  { id: 'impresa',      label: 'Impresa',      icon: '📋', sottotitolo: 'DURC, visura, INAIL, INPS, Cassa Edile' },
  { id: 'attestati',    label: 'Attestati',    icon: '🎓', sottotitolo: 'Corsi formazione operai' },
  { id: 'sanitaria',    label: 'Sanità',       icon: '🩺', sottotitolo: 'Sorveglianza sanitaria operai' },
  { id: 'attrezzature', label: 'Attrezzature', icon: '🏗️', sottotitolo: 'Libretti, collaudi, verifiche' },
];

// Categorie predefinite
const CATEGORIE_IMPRESA = ['durc', 'visura_camerale', 'inail', 'inps', 'cassa_edile'];
const CATEGORIE_ATTREZZATURE_SUGGERITE = ['ponteggio', 'libretti_attrezzature', 'collaudi', 'verifiche_periodiche'];

const CATEGORIE_LABEL = {
  durc: 'DURC',
  visura_camerale: 'Visura camerale',
  inail: 'INAIL',
  inps: 'INPS',
  cassa_edile: 'Cassa Edile',
  ponteggio: 'Libretto Ponteggio',
  libretti_attrezzature: 'Libretti Attrezzature',
  collaudi: 'Collaudi',
  verifiche_periodiche: 'Verifiche periodiche',
};

function umanizzaCategoria(key) {
  if (CATEGORIE_LABEL[key]) return CATEGORIE_LABEL[key];
  return key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function formattaSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function formattaData(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return iso; }
}

// ============================================================================
// COMPONENTE PRINCIPALE
// ============================================================================
export default function ArchivioDocumenti({ onIndietro }) {

  const [tab, setTab] = useState('impresa');
  const [albero, setAlbero] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState(null);
  const [storicoApertoPer, setStoricoApertoPer] = useState({});
  const [uploadInCorso, setUploadInCorso] = useState(null); // chiave categoria in upload
  const [ultimoUpload, setUltimoUpload] = useState(null); // {tipoDoc, categoria, filename, ts}

  // Modal "nuova categoria attrezzature"
  const [modalNuovaCat, setModalNuovaCat] = useState(false);
  const [nuovaCatNome, setNuovaCatNome] = useState('');

  useEffect(() => {
    caricaLista();
  }, []);

  async function caricaLista() {
    setLoading(true);
    setErrore(null);
    try {
      const token = localStorage.getItem('bertoc_jwt');
      if (!token) {
        setErrore('Non sei autenticato. Effettua il login.');
        setLoading(false);
        return;
      }
      const resp = await fetch(`${NAS_URL}/api/documenti/lista`, {
        headers: { 'Authorization': 'Bearer ' + token }
      });
      if (resp.status === 401) {
        setErrore('Sessione scaduta. Effettua di nuovo il login.');
        return;
      }
      if (!resp.ok) throw new Error('HTTP ' + resp.status);
      const data = await resp.json();
      setAlbero(data);
    } catch (e) {
      console.error('[ARCHIVIO] Errore:', e);
      setErrore('Errore caricamento: ' + e.message);
    } finally {
      setLoading(false);
    }
  }

  function apriPdf(tipoDoc, categoria, sottoCartella, filename) {
    const token = localStorage.getItem('bertoc_jwt');
    const path = `${tipoDoc}/${categoria}/${sottoCartella}/${filename}`;
    fetch(`${NAS_URL}/api/documenti/file?path=${encodeURIComponent(path)}`, {
      headers: { 'Authorization': 'Bearer ' + token }
    })
      .then(r => {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.blob();
      })
      .then(blob => {
        // Crea un link nascosto e clicca per aprire il PDF
        // Safari blocca window.open dopo fetch async, ma <a target="_blank"> funziona
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.download = filename; // se Safari blocca apertura, scarica direttamente
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
      })
      .catch(e => alert('Errore apertura PDF: ' + e.message));
  }

  // UPLOAD FILE
  async function uploadFile(tipoDoc, categoria, file) {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setErrore('File troppo grande (max 10 MB)');
      return;
    }
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setErrore('Solo file PDF accettati');
      return;
    }
    const chiave = `${tipoDoc}/${categoria}`;
    setUploadInCorso(chiave);
    setErrore(null);
    try {
      const token = localStorage.getItem('bertoc_jwt');
      if (!token) throw new Error('Non sei autenticato');

      const formData = new FormData();
      formData.append('file', file);
      formData.append('tipoDoc', tipoDoc);
      formData.append('categoria', categoria);
      formData.append('filename', file.name);

      const resp = await fetch(`${NAS_URL}/api/documenti/upload`, {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + token },
        body: formData,
      });

      if (resp.status === 401) {
        setErrore('Sessione scaduta. Rifai login.');
        return;
      }
      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.error || 'HTTP ' + resp.status);
      }

      const data = await resp.json();
      console.log('[ARCHIVIO] Upload OK:', data);

      setUltimoUpload({
        tipoDoc, categoria,
        filename: data.filename,
        ts: Date.now(),
      });

      // Refresh lista
      await caricaLista();

    } catch (e) {
      console.error('[ARCHIVIO] Errore upload:', e);
      setErrore('Errore upload: ' + e.message);
    } finally {
      setUploadInCorso(null);
    }
  }

  // Crea nuova categoria attrezzature (solo lato UI - servira' un upload per crearla davvero)
  function aggiungiCategoria() {
    const nome = nuovaCatNome.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_');
    if (!nome) {
      setModalNuovaCat(false);
      return;
    }
    setAlbero({
      ...albero,
      attrezzature: {
        ...(albero?.attrezzature || {}),
        [nome]: { attuale: [], storico: [] },
      },
    });
    setModalNuovaCat(false);
    setNuovaCatNome('');
  }

  function contaDocumenti() {
    if (!albero) return { totali: 0, perTab: {} };
    const perTab = {};
    let totali = 0;
    TABS.forEach(t => {
      let count = 0;
      const tipo = albero[t.id] || {};
      Object.values(tipo).forEach(cat => {
        if (cat.attuale) count += cat.attuale.length;
      });
      perTab[t.id] = count;
      totali += count;
    });
    return { totali, perTab };
  }

  if (loading && !albero) {
    return (
      <div className="content" style={{ textAlign: "center", padding: "60px 20px" }}>
        <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 22, color: theme.accent }}>
          📁 Archivio Documenti
        </div>
        <div style={{ color: theme.textMuted, marginTop: 20 }}>Caricamento...</div>
      </div>
    );
  }

  const conta = contaDocumenti();
  const tabAttiva = TABS.find(t => t.id === tab);
  const datiTab = albero?.[tab] || {};

  // Per Impresa: garantisci che le 5 categorie predefinite siano sempre mostrate
  let categorieTab = Object.keys(datiTab);
  if (tab === 'impresa') {
    CATEGORIE_IMPRESA.forEach(c => {
      if (!categorieTab.includes(c)) categorieTab.push(c);
    });
  }
  categorieTab.sort();

  const supportaUpload = tab === 'impresa' || tab === 'attrezzature';

  return (
    <div className="content" style={{ maxWidth: 1000, margin: "0 auto", padding: "0 8px" }}>

      {/* HEADER */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, paddingBottom: 16, borderBottom: `1px solid ${theme.border}` }}>
        <div>
          <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 24, color: theme.accent }}>
            📁 Archivio Documenti
          </div>
          <div style={{ fontSize: 13, color: theme.textMuted, marginTop: 4 }}>
            Documenti aziendali, attestati, sorveglianza sanitaria, attrezzature
          </div>
        </div>
        <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 8, padding: "8px 14px", fontSize: 13, color: theme.text, fontWeight: 600 }}>
          {conta.totali} {conta.totali === 1 ? 'documento' : 'documenti'} totali
        </div>
      </div>

      {errore && (
        <div style={{ background: "#2a0f12", border: `1px solid ${theme.error}44`, borderRadius: 10, padding: "12px 16px", marginBottom: 16, fontSize: 13, color: theme.error, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>⚠️ {errore}</span>
          <button onClick={caricaLista} style={{ ...btnSecondarioSt, marginLeft: 12, padding: "4px 10px", fontSize: 12 }}>
            Riprova
          </button>
        </div>
      )}

      {ultimoUpload && (Date.now() - ultimoUpload.ts < 4000) && (
        <div style={{ background: "#0a2218", border: `1px solid ${theme.success}44`, borderRadius: 10, padding: "12px 16px", marginBottom: 16, fontSize: 13, color: theme.success }}>
          ✅ Caricato: <strong>{ultimoUpload.filename}</strong> in {ultimoUpload.tipoDoc}/{ultimoUpload.categoria}
        </div>
      )}

      {/* TABS */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        {TABS.map(t => {
          const attiva = t.id === tab;
          const count = conta.perTab[t.id] || 0;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                flex: "1 1 200px",
                minWidth: 180,
                padding: "12px 16px",
                background: attiva ? theme.accent : theme.surface,
                color: attiva ? "#1a1d27" : theme.text,
                border: `1px solid ${attiva ? theme.accent : theme.border}`,
                borderRadius: 10,
                cursor: "pointer",
                fontFamily: "'DM Sans',sans-serif",
                fontWeight: attiva ? 700 : 500,
                fontSize: 14,
                textAlign: "left",
                transition: "all 0.15s",
              }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span><span style={{ marginRight: 6 }}>{t.icon}</span>{t.label}</span>
                <span style={{
                  background: attiva ? "rgba(0,0,0,0.15)" : theme.bg,
                  color: attiva ? "#1a1d27" : theme.textMuted,
                  borderRadius: 12, padding: "2px 8px", fontSize: 11, fontWeight: 700,
                }}>
                  {count}
                </span>
              </div>
              <div style={{ fontSize: 11, opacity: 0.75, marginTop: 4, fontWeight: 400 }}>
                {t.sottotitolo}
              </div>
            </button>
          );
        })}
      </div>

      {/* INFO BANNER per tab non supportate */}
      {!supportaUpload && (
        <div style={{ ...cardSt, background: theme.bg, border: `1px dashed ${theme.border}`, textAlign: "center", padding: 30, color: theme.textMuted }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>{tabAttiva.icon}</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: theme.text, marginBottom: 8 }}>
            {tabAttiva.label}
          </div>
          <div style={{ fontSize: 13 }}>
            L'upload per questa sezione sarà disponibile a breve.<br/>
            Richiede di selezionare il lavoratore di riferimento.
          </div>
        </div>
      )}

      {/* CONTENUTO TAB */}
      {supportaUpload && (
        <div>
          {tab === 'attrezzature' && (
            <div style={{ marginBottom: 12, textAlign: "right" }}>
              <button onClick={() => setModalNuovaCat(true)} style={btnSecondarioSt}>
                + Nuova categoria
              </button>
            </div>
          )}

          {categorieTab.length === 0 && (
            <div style={{ ...cardSt, textAlign: "center", padding: 40, color: theme.textMuted }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>📭</div>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>
                Nessuna categoria in {tabAttiva.label}
              </div>
              <div style={{ fontSize: 13, color: theme.textDim }}>
                {tab === 'attrezzature' && 'Clicca "+ Nuova categoria" sopra per iniziare'}
              </div>
            </div>
          )}

          {categorieTab.map(catKey => {
            const cat = datiTab[catKey] || { attuale: [], storico: [] };
            const attuale = cat.attuale || [];
            const storico = cat.storico || [];
            const storicoAperto = storicoApertoPer[`${tab}/${catKey}`];
            const chiaveUpload = `${tab}/${catKey}`;
            const inUpload = uploadInCorso === chiaveUpload;

            return (
              <CategoriaCard
                key={catKey}
                tipoDoc={tab}
                categoriaKey={catKey}
                attuale={attuale}
                storico={storico}
                storicoAperto={storicoAperto}
                inUpload={inUpload}
                onApri={(sottoCart, filename) => apriPdf(tab, catKey, sottoCart, filename)}
                onUpload={(file) => uploadFile(tab, catKey, file)}
                onToggleStorico={() => setStoricoApertoPer({
                  ...storicoApertoPer,
                  [`${tab}/${catKey}`]: !storicoAperto
                })}
              />
            );
          })}
        </div>
      )}

      {/* MODAL nuova categoria attrezzature */}
      {modalNuovaCat && (
        <div onClick={() => setModalNuovaCat(false)} style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 1000,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 12,
            padding: 24, maxWidth: 400, width: "90%",
          }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: theme.text, marginBottom: 14 }}>
              Nuova categoria attrezzature
            </div>
            <div style={{ fontSize: 13, color: theme.textMuted, marginBottom: 12 }}>
              Esempi: ponteggio, libretti_attrezzature, collaudi
            </div>
            <input
              type="text"
              value={nuovaCatNome}
              onChange={e => setNuovaCatNome(e.target.value)}
              placeholder="es. ponteggio"
              autoFocus
              style={{
                width: "100%",
                padding: "10px 12px",
                background: theme.bg,
                border: `1px solid ${theme.border}`,
                borderRadius: 8,
                color: theme.text,
                fontSize: 14,
                marginBottom: 16,
                outline: "none",
                boxSizing: "border-box",
              }}
            />
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => { setModalNuovaCat(false); setNuovaCatNome(''); }} style={btnSecondarioSt}>
                Annulla
              </button>
              <button onClick={aggiungiCategoria} style={btnPrimarioSt}>
                Aggiungi
              </button>
            </div>
          </div>
        </div>
      )}

      {onIndietro && (
        <button onClick={onIndietro} style={{ ...btnSecondarioSt, marginTop: 20, width: "100%" }}>
          ← Torna al menu
        </button>
      )}
    </div>
  );
}

// ============================================================================
// Card categoria con upload
// ============================================================================
function CategoriaCard({ tipoDoc, categoriaKey, attuale, storico, storicoAperto, inUpload, onApri, onUpload, onToggleStorico }) {
  const fileInputRef = useRef(null);

  function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (file) onUpload(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  return (
    <div style={cardSt}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: theme.text }}>
            {umanizzaCategoria(categoriaKey)}
          </div>
          <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 2 }}>
            {attuale.length} attuale, {storico.length} storico
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {attuale.length === 0 && (
            <span style={{ fontSize: 11, color: theme.textDim, background: theme.bg, padding: "4px 10px", borderRadius: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
              Vuoto
            </span>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={inUpload}
            style={{ ...btnPrimarioSt, opacity: inUpload ? 0.5 : 1 }}>
            {inUpload ? "Caricamento..." : "+ Carica"}
          </button>
        </div>
      </div>

      {attuale.length > 0 && (
        <div>
          {attuale.map(f => (
            <FileRow
              key={f.name}
              file={f}
              label="ATTUALE"
              colore={theme.success}
              onApri={() => onApri('attuale', f.name)}
            />
          ))}
        </div>
      )}

      {storico.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <button
            onClick={onToggleStorico}
            style={{ ...btnSecondarioSt, width: "100%", padding: "8px 12px", fontSize: 12, color: theme.textMuted }}>
            {storicoAperto ? '▲' : '▼'} Storico ({storico.length} {storico.length === 1 ? 'versione' : 'versioni'})
          </button>
          {storicoAperto && (
            <div style={{ marginTop: 8 }}>
              {storico.map(f => (
                <FileRow
                  key={f.name}
                  file={f}
                  label="STORICO"
                  colore={theme.textDim}
                  onApri={() => onApri('storico', f.name)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FileRow({ file, label, colore, onApri }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
      padding: "10px 12px", background: theme.bg, border: `1px solid ${theme.border}`,
      borderRadius: 8, marginBottom: 6,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 }}>
        <span style={{ fontSize: 18 }}>📄</span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: theme.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {file.name}
          </div>
          <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>
            <span style={{ color: colore, fontWeight: 600 }}>{label}</span>
            {' • '}
            {formattaSize(file.size)}
            {' • '}
            {formattaData(file.mtime)}
          </div>
        </div>
      </div>
      <button onClick={onApri} style={{ ...btnSecondarioSt, padding: "6px 12px", fontSize: 12 }}>
        Apri PDF
      </button>
    </div>
  );
}
